import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import type { TEvent, TEventFormData } from '../../types/event';
import { eventCreate, eventDelete, eventsList } from '../../services/event';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import Button from '../../components/ui/button/Button';
import { ListIcon, PlusIcon, TrashBinIcon } from '../../icons';
import { useToggle } from '../../hooks/useToggle';
import { CreateEventModal } from '../../components/events/create-modal';
import PageMeta from '../../components/common/PageMeta';
import { toast } from 'react-toastify';
import { useSimplePagination } from '../../hooks/useSimplePagination';
import CommonTable, {
  type ActionConfig,
  type ColumnConfig,
} from '../../components/common/Table';
import moment from 'moment';
import { useNavigate } from 'react-router';
import { PAGINATION_PER_PAGE } from '../../utils/constants';
import debounce from 'lodash.debounce';
import Input from '../../components/form/input/InputField';

const columns: ColumnConfig<TEvent>[] = [
  {
    key: 'id',
    header: 'ID',
  },
  {
    key: 'name',
    header: 'Name',
  },
  {
    key: 'description',
    header: 'Description',
    className: 'max-w-64 truncate',
  },
  {
    key: 'time',
    header: 'Time',
    render: (item) => moment(item.time).format('ddd D/M/YYYY - HH:MM'),
  },
  {
    key: 'scope',
    header: 'Scope',
  },
  {
    key: 'registrationLink',
    header: 'Registration Link',
    render: (item) => item.registrationLink ?? 'N/A',
  },
  {
    key: 'virtualLink',
    header: 'Virtual Link',
    render: (item) => item.virtualLink ?? 'N/A',
  },
  {
    key: 'createdAt',
    header: 'Created At',
    render: (item) => new Date(item.createdAt).toLocaleString(),
  },
];

const Events = () => {
  const [events, setEvents] = useState<TEvent[]>([]);
  const [query, setQuery] = useState('');

  const {
    state: createModalState,
    on: showCreateModal,
    off: hideCreateModal,
  } = useToggle();

  const { state: isLoading, on: showLoader, off: hideLoader } = useToggle();
  const {
    state: isCreating,
    on: showCreatingLoad,
    off: hideCreatingLoad,
  } = useToggle();

  const { currentPage, totalPages, onPageChange, setTotalPages } =
    useSimplePagination();
  const navigate = useNavigate();

  const fetchEvents = async () => {
    if (isLoading) return;
    showLoader();
    const res = await eventsList({
      params: {
        query,
        page: currentPage,
        limit: PAGINATION_PER_PAGE,
      },
    }).finally(() => {
      hideLoader();
    });
    setEvents(
      res?.rows?.map((item) => ({
        ...item,
        time: item.time.replace('.000Z', ''),
      })) ?? []
    );
    setTotalPages(res.count);
  };

  const debouncedResults = useMemo(() => {
    return debounce(fetchEvents, 500);
    // eslint-disable-next-line
  }, [query, currentPage]);

  const onDelete = async (id: string, name: string) => {
    const confirmation = window.confirm(
      `Are you sure you want to delete ${name}?`
    );
    if (!confirmation) return;
    await eventDelete(id);
    fetchEvents();
  };

  const onCreate = async (
    values: TEventFormData,
    successCallback?: VoidFunction
  ) => {
    if (isCreating) {
      return;
    }
    try {
      showCreatingLoad();
      await eventCreate({ ...values });
      toast.success('Event Added Successfully');
      successCallback?.();
      fetchEvents();
    } catch (e: any) {
      // toast.error()
      console.error(e);
    } finally {
      hideCreatingLoad();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  useEffect(() => {
    debouncedResults();
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  const actions: ActionConfig<TEvent>[] = [
    {
      label: <ListIcon />,
      onClick: (item) => navigate(`/events/${item.id}/statuses`),
      variant: 'primary',
      size: 'sm',
      className: () => 'hover:cursor-pointer',
      title: 'View Statuses',
    },
    {
      label: <TrashBinIcon className="text-red-400" />,
      onClick: (item) => onDelete(item.id, item.name),
      variant: 'outline',
      size: 'sm',
      className: () => 'hover:cursor-pointer',
      title: 'View Statuses',
    },
  ];

  return (
    <div>
      <PageMeta title="Events" description="List of events" />
      <PageBreadcrumb pageTitle="Events" />
      <div className="flex justify-end items-center my-5 gap-5">
        <Input
          placeholder="Search by Name or Email"
          value={query}
          onChange={handleChange}
          wrapperClass="w-full"
        />
        <Button size="md" onClick={showCreateModal} startIcon={<PlusIcon />}>
          Create
        </Button>
      </div>
      <CommonTable
        columns={columns}
        data={events}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        actions={actions}
        showPagination
      />
      <CreateEventModal
        isOpen={createModalState}
        onClose={hideCreateModal}
        onSubmit={onCreate}
        isLoading={isCreating}
      />
    </div>
  );
};

export default Events;
